import { v4 } from "uuid";
import type { HypotheticalFlowDataSet } from "@/libs/calculations/predictiveCalcs/getPredictedDesiredFlowRateSTBD";
import type { SerializedScenario } from "./tornadoChartJobs";
import { getPredictionPool } from "./tornadoPools";
import { getPersistentStore, ResultCache } from "./tornadoResultCache";

// Each frequency's flow-rate prediction depends only on the scenario's
// calculation inputs and the frequency (not the chart range or units), so
// predictions are cached and shared one frequency at a time: widening the
// chart range only predicts the new frequencies, and the chart and the
// prefetch share any prediction already running.

const predictionCache = new ResultCache<HypotheticalFlowDataSet>({
  name: "predictions",
  memorySize: 120,
  persistentSize: 400,
  store: getPersistentStore()
});

type InFlight = {
  promise: Promise<HypotheticalFlowDataSet>;
  refs: number;
  released: boolean;
  cancel?: () => void;
};
const inFlight = new Map<string, InFlight>();

export type PredictionLease = {
  promise: Promise<HypotheticalFlowDataSet>;
  // Call when the prediction is no longer needed. The job is cancelled once
  // every holder has released it before it finished.
  release: () => void;
};

const abort = () =>
  new DOMException("Tornado prediction cancelled", "AbortError");

export const acquirePrediction = (
  scenarioHash: string,
  scenario: SerializedScenario,
  frequency: number
): PredictionLease => {
  const key = `${scenarioHash}:${frequency}`;
  const cached = predictionCache.peek(key);
  if (cached) return { promise: Promise.resolve(cached), release: () => {} };

  let entry = inFlight.get(key);
  if (!entry) {
    const created: InFlight = {
      refs: 0,
      released: false,
      promise: Promise.resolve() as never
    };
    created.promise = (async () => {
      const stored = await predictionCache.get(key);
      if (stored) return stored;
      if (created.released) throw abort();
      const task = getPredictionPool().run({
        id: v4(),
        type: "predict",
        scenario,
        frequency
      });
      created.cancel = task.cancel;
      const value = (await task.promise) as HypotheticalFlowDataSet;
      predictionCache.set(key, value);
      return value;
    })().finally(() => {
      if (inFlight.get(key) === created) inFlight.delete(key);
    });
    created.promise.catch(() => undefined);
    inFlight.set(key, created);
    entry = created;
  }

  const held = entry;
  held.refs++;
  let released = false;
  return {
    promise: held.promise,
    release: () => {
      if (released) return;
      released = true;
      held.refs--;
      if (held.refs === 0) {
        held.released = true;
        held.cancel?.();
        if (inFlight.get(key) === held) inFlight.delete(key);
      }
    }
  };
};
